import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation deleteUser($id: String!) {
    deleteUser(id: $id) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const deleteUserRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {id: String;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(
  'user/delete_user', async (params: {id: String;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.deleteUser;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  });

const deleteUserSlice = createSlice({
  name: 'delete_user',
  initialState: initialState,
  reducers: {
    deleteUserClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(deleteUserRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(deleteUserRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(deleteUserRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { deleteUserClear } = deleteUserSlice.actions;

export const deleteUser = createSelector(
  (state: RootState) => state.user.deleteUser.result,
  (state: RootState) => state.user.deleteUser.error,
  (state: RootState) => state.user.deleteUser.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default deleteUserSlice.reducer;